import { useEffect, useState } from 'react';
import { supabase } from '../lib/supabase';
import { FILTRO_VIDEO_PG, esVideoDeFila } from '../utils/filtroContenido';
import { orDeMarca } from '../utils/filtroDeMarca';
import { firmarEnLote } from '../utils/firmarEnLote';
import { ratioDeFormato } from '../utils/ratioDeFormato';
import { acabadoPorId } from '../pages/v2/variantesAcabado';

/**
 * Últimas creaciones REALES del usuario — ADR-2026-07-31-shell-nuevo-datos-reales.
 *
 * La home nueva mostraba `CREACIONES_RECIENTES` de `contenidoAtlas`, que son las
 * imágenes de demo del alta (`GENERATION_RESULTS`) y de los ejemplos por modo. Eso
 * era correcto para un preview —había que comparar tres estilos con contenido, no
 * con media pantalla en blanco— pero en la plataforma es una sección que se llama
 * "Lo último que generaste" y muestra cosas que el usuario nunca generó.
 *
 * Lee lo MISMO que `/redes-sociales/imagenes` (`GeneratedImagesHistory`): la tabla
 * `imagenes_generadas` filtrada por empresa y `active = true`, y las miniaturas del
 * bucket `generadas` bajo `{idCompany}/opt/{file_name}`, con el `full/` como
 * respaldo cuando el thumbnail todavía no existe. Los mismos paths y la misma
 * cache de URLs firmadas que el historial, así que abrir la home no vuelve a
 * firmar lo que el historial ya firmó (y al revés).
 */

export interface CreacionReciente {
  /** `imagenes_generadas.id` (uuid). Se usa para abrirla en el historial. */
  id: string;
  /** URL firmada de la miniatura (o del full si no hay `opt/`). */
  src: string;
  alt: string;
  /** `true` cuando el resultado es un video — se le pinta el play encima. */
  esVideo: boolean;
  /**
   * Proporción ancho/alto, para dibujar la miniatura con la forma que tiene en vez
   * de recortarla a una fija — ADR-2026-08-03-creaciones-recientes-respetan-su-forma.
   *
   * Sale de `imagenes_generadas.formato` ("4:5", "9:16", "1:1"…), que se guarda al
   * generar. `null` cuando la fila no lo trae (una de cada cuatro): ahí la mide el
   * componente cuando la imagen carga.
   *
   * ⚠️ **En los videos es sólo una apuesta.** Lo que se guarda es el formato que
   * pidió el flujo, no el del archivo: los de "animar imagen" heredan la forma de la
   * imagen de origen y aun así quedan anotados como `9:16`
   * (ADR-2026-08-03-el-formato-de-los-videos-no-es-de-fiar). Quien dibuje un video
   * tiene que medir la miniatura y quedarse con lo medido.
   */
  ratio: number | null;
  /**
   * Qué toque de "Generar" la produjo — `imagenes_generadas.execution_id`.
   *
   * Es la clave de LOTE (ADR-2026-08-25-lotes-con-fecha-relativa): el backend
   * escribe una fila por imagen y todas las de una misma corrida comparten este
   * uuid. `null` en ~6% de las filas recientes (12% históricas), que son las de
   * flujos que no lo guardan; ahí el lote se deduce por cercanía de `creadaEn`.
   */
  loteId: string | null;
  /** `created_at` crudo (ISO). Encabeza el lote como fecha relativa. */
  creadaEn: string;
}

interface FilaGenerada {
  id: string;
  file_name: string | null;
  tipo: string | null;
  formato: string | null;
  /**
   * Proporción de los VIDEOS — ADR-2026-08-03-ratio-de-los-videos-sale-de-datos-generacion.
   *
   * La columna `formato` la llena el flujo de imágenes; el de video guarda lo suyo
   * dentro de `datos_generacion`, con dos nombres según por dónde se generó
   * (`Format` en el flujo viejo, `aspect_ratio` en el de influencer). Sobre 2.225
   * videos activos: 76 tienen `formato`, 1.381 sólo `Format` y 35 sólo
   * `aspect_ratio`.
   *
   * Se piden como claves sueltas del jsonb y NO la columna entera: `datos_generacion`
   * llega hasta 475 KB en una fila (lleva prompts y escenas), y traerla para leer un
   * "9:16" convertiría la carga de la home en megabytes.
   */
  fmt_gen: string | null;
  fmt_gen_alt: string | null;
  /**
   * El acabado con el que se generó, en "Variantes de acabado" — el backend lo guarda
   * por fila (`datos_generacion.acabado_id`). Es lo ÚNICO que distingue una variante de
   * la de al lado: son el mismo mueble, y sin esto las doce cards dicen todas lo mismo.
   * `null` en cualquier otro modo. Se pide como clave suelta por el mismo motivo que los
   * formatos: la columna entera pesa cientos de KB.
   */
  acabado_id: string | null;
  created_at: string;
  /** Uuid de la corrida que generó la fila — ver `CreacionReciente.loteId`. */
  execution_id: string | null;
  variantes?: { nombre?: string | null } | null;
}

/** El nombre del acabado, si la fila trae uno del catálogo curado. */
const nombreDeAcabado = (id: string | null): string | undefined =>
  (id ? acabadoPorId(id)?.nombre : undefined) ?? undefined;

/**
 * Mismo criterio que el historial: por `tipo`, con la extensión como respaldo.
 *
 * Vive en `utils/filtroContenido` y no acá porque el filtro "sólo videos" necesita la
 * MISMA regla escrita como `where` de PostgREST, y las dos tienen que moverse juntas
 * (ADR-2026-09-08-creador-de-video-filtra-por-tipo-de-contenido).
 */
const esVideo = esVideoDeFila;

export interface CreacionesRecientes {
  items: CreacionReciente[];
  cargando: boolean;
  /**
   * Hay creaciones más allá de las `limite` que se devuelven —
   * ADR-2026-08-03-ver-mas-solo-si-hay-algo-mas.
   *
   * Se sabe pidiendo UNA de más y devolviéndola de menos: si volvieron `limite + 1`,
   * hay al menos una que no entró en `items`. Sin esto, "traje 6" y "hay exactamente
   * 6" son indistinguibles, y la home no puede decidir si mostrar "Ver más".
   */
  hayMas: boolean;
}

/**
 * @param idCompany Empresa del usuario. Mientras sea `null` el hook no consulta —
 *   lo resuelve `useCuentaAtlas`, que es quien ya leyó `profiles`.
 * @param limite Cuántas traer. La home muestra 6.
 * @param idMarca Marca activa. Con marca, la sección muestra sólo lo generado para
 *   ELLA — ADR-2026-08-03-creaciones-recientes-por-marca. `null` = sin filtro, que es
 *   lo que corresponde mientras la marca todavía no resolvió y en cuentas de una
 *   sola marca.
 */
export function useCreacionesRecientes(
  idCompany: string | null,
  limite = 6,
  idMarca: string | null = null,
  /**
   * Cambiá este número para volver a pedir la lista.
   *
   * Lo usa la columna de resultados del modo cuando una tanda cierra sin imágenes que
   * dibujar (el preload no alcanzó a resolverlas): las filas YA están en la base, así
   * que en vez de irse a otra pantalla se vuelve a preguntar y entran por acá
   * (ADR-2026-09-04-la-generacion-del-modo-nunca-cambia-de-pantalla).
   */
  recargarToken = 0,
  /**
   * Traer SÓLO videos — ADR-2026-09-08-creador-de-video-filtra-por-tipo-de-contenido.
   *
   * Lo prende el filtro de Creador de video. Va en la CONSULTA y no en el resultado: los
   * videos son una minoría del historial, así que filtrar lo ya traído devolvía páginas
   * enteras vacías y la paginación se quedaba trayendo fotos para tirarlas.
   */
  soloVideos = false,
): CreacionesRecientes {
  const [items, setItems] = useState<CreacionReciente[]>([]);
  const [hayMas, setHayMas] = useState(false);
  const [cargando, setCargando] = useState(true);

  useEffect(() => {
    if (!idCompany) {
      // Sin empresa todavía no se puede afirmar que no haya creaciones: se sigue
      // considerando "cargando" para que la home no parpadee el estado vacío
      // antes de tiempo.
      return;
    }
    let cancelado = false;

    (async () => {
      try {
        setCargando(true);
        // La marca no está en `imagenes_generadas`: se deduce del producto, así que
        // filtrarla obliga a subir por `variantes → productos.brand_id` con dos
        // `!inner`. Es el MISMO camino que usa el filtro del historial
        // (`GeneratedImagesHistory`), a propósito: si las dos pantallas dedujeran la
        // marca distinto, la preview de la home y su "Ver más" mostrarían conjuntos
        // que no coinciden.
        //
        //
        // La marca es una columna propia desde ADR-2026-09-13-brand-id-en-imagenes-generadas:
        // antes se deducía subiendo `variantes → productos.brand_id` con dos `!inner`, y
        // las generaciones sin producto (todos los modos de prompt) se caían del filtro.
        // El embed de `variantes` queda por el nombre, pero ya no es `!inner`.
        //
        // "De esta marca" es `brand_id = X` **o sin marca** (`orDeMarca`,
        // ADR-2026-09-13-las-generaciones-sin-marca-se-ven-en-todas): lo que no se puede
        // atribuir a ninguna marca se ve desde todas, como los productos huérfanos —
        // escondido bajo todas, el usuario cree que se le borró.
        const select =
          'id, file_name, tipo, formato, fmt_gen:datos_generacion->>Format, fmt_gen_alt:datos_generacion->>aspect_ratio, acabado_id:datos_generacion->>acabado_id, created_at, execution_id, variantes:variantes(nombre)';
        let query = supabase
          .from('imagenes_generadas')
          .select(select)
          .eq('id_company', idCompany)
          .eq('active', true);
        if (idMarca) query = query.or(orDeMarca(idMarca));
        // Dos `or` en la misma consulta (marca y videos) son dos parámetros `or=` que
        // PostgREST combina con AND — verificado contra la API (200; un `or` mal
        // formado devuelve 400). No se pisan.
        if (soloVideos) query = query.or(FILTRO_VIDEO_PG);
        const { data, error } = await query
          .order('created_at', { ascending: false })
          // Una de más que las que se devuelven: es el sondeo de `hayMas`.
          .range(0, limite);

        if (error) throw error;
        if (cancelado) return;

        const filas = ((data ?? []) as unknown as FilaGenerada[]).filter(
          (f) => !!f.file_name,
        );

        // Los dos paths de cada fila, en el mismo orden que `filas`.
        const paths = filas.map((fila) => {
          const fileName = fila.file_name as string;
          // El thumbnail de un video es el PNG homónimo, igual que en el
          // historial: el bucket no guarda un `opt/` en mp4.
          const nombreThumb = esVideo(fila)
            ? fileName.replace(/\.(mp4|webm|mov)$/i, '.png')
            : fileName;
          return {
            opt: `${idCompany}/opt/${nombreThumb}`,
            full: `${idCompany}/full/${fileName}`,
          };
        });

        // UN pedido para todas las miniaturas (ver `firmarEnLote`).
        const firmadas = await firmarEnLote(paths.map((p) => p.opt));
        if (cancelado) return;

        // Sin `opt/` (carrera con el final del job, o generaciones viejas) se cae al
        // full, en una SEGUNDA tanda que sólo lleva las que faltaron —normalmente
        // ninguna, y entonces no sale ni un pedido más. Para un video el full es el
        // mp4, que como `src` de un `<img>` no pinta nada: ése ni se pide, y la fila
        // se descarta abajo en vez de dejar un hueco roto en la grilla.
        const faltantes = filas.flatMap((fila, i) =>
          !firmadas.has(paths[i].opt) && !esVideo(fila) ? [paths[i].full] : [],
        );
        const firmadasFull = await firmarEnLote(faltantes);
        if (cancelado) return;

        const utiles = filas.flatMap((fila, i): CreacionReciente[] => {
          const src =
            firmadas.get(paths[i].opt) ?? firmadasFull.get(paths[i].full) ?? '';
          if (!src) return [];
          return [
            {
              id: fila.id,
              src,
              // El acabado gana al nombre del producto: en "Variantes" todas las cards
              // salen del MISMO mueble, así que "Mesa" ×12 no dice nada y "Terracota" sí.
              // Un acabado subido por el usuario no está en el catálogo curado: ahí no hay
              // nombre que mostrar y cae al de siempre.
              alt:
                nombreDeAcabado(fila.acabado_id) ??
                fila.variantes?.nombre ??
                'Creación de Atlas',
              esVideo: esVideo(fila),
              // Cadena de respaldo: la columna primero (es la de las imágenes) y
              // después las dos claves donde lo guarda el flujo de video. Lo que no
              // resuelva por acá lo mide el componente cuando carga la miniatura.
              ratio:
                ratioDeFormato(fila.formato) ??
                ratioDeFormato(fila.fmt_gen) ??
                ratioDeFormato(fila.fmt_gen_alt),
              loteId: fila.execution_id ?? null,
              creadaEn: fila.created_at,
            },
          ];
        });
        // El sondeo se hace sobre las filas CRUDAS y no sobre `utiles`: una fila
        // puede caerse acá porque su archivo no resolvió, y eso no significa que no
        // haya más creaciones en la base.
        setHayMas(filas.length > limite);
        setItems(utiles.slice(0, limite));
      } catch {
        // Falla cerrado: sin creaciones que mostrar, la home pinta el estado
        // vacío en vez de romperse.
        if (!cancelado) {
          setItems([]);
          setHayMas(false);
        }
      } finally {
        if (!cancelado) setCargando(false);
      }
    })();

    return () => {
      cancelado = true;
    };
    // `idMarca` en las deps: cambiar de marca en la sidebar tiene que volver a pedir,
    // que es todo el punto del filtro. `soloVideos`, por lo mismo: es otra consulta.
  }, [idCompany, limite, idMarca, recargarToken, soloVideos]);

  return { items, cargando, hayMas };
}

export default useCreacionesRecientes;
